import { useCallback, useEffect, useRef, useState } from "react";
import { useVisualViewport } from "../../../infrastructure/hooks/useVisualViewport";
import { useExerciseSearch } from "./useExerciseSearch";

/** Preferred height of the results list; shrinks when the visible area is short (keyboard up). */
const DROPDOWN_MAX_HEIGHT_PX = 320;
const DROPDOWN_MIN_HEIGHT_PX = 140;
/** Room kept for the input itself + the dialog header above the list. */
const DROPDOWN_RESERVED_PX = 180;

interface IUseExercisePickerArgs {
  eventId?: number;
  planId?: number;
  onPickFromCatalog: (catalogExerciseId: number, exerciseName: string) => Promise<void> | void;
  onCreateAndAdd: (name: string) => Promise<void> | void;
}

/**
 * Owns the exercise picker's open/closed state and the search it drives.
 *
 * The dropdown is open only while the input is focused. Picking an item (or creating a
 * new exercise) both clears the search and *closes* the picker — clearing alone would
 * reopen the full browse list because "focused + empty query" means "browse". Tapping
 * anywhere outside the picker closes it too (mouse and touch), and the input is blurred
 * so the mobile keyboard goes away with it.
 */
export const useExercisePicker = ({
  eventId,
  planId,
  onPickFromCatalog,
  onCreateAndAdd,
}: IUseExercisePickerArgs) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { height: visibleHeight, isKeyboardOpen } = useVisualViewport();

  const { query, setQuery, results, hasSearched, exactMatchExists, clear } =
    useExerciseSearch({ eventId, planId, isOpen });

  const close = useCallback(() => {
    setIsOpen(false);
    inputRef.current?.blur();
  }, []);

  const open = useCallback(() => setIsOpen(true), []);

  const pickFromCatalog = useCallback(
    (exerciseId: number, exerciseName: string) => {
      clear();
      close();
      void onPickFromCatalog(exerciseId, exerciseName);
    },
    [clear, close, onPickFromCatalog],
  );

  const createAndAdd = useCallback(() => {
    const value = query.trim();
    if (!value) return;
    clear();
    close();
    void onCreateAndAdd(value);
  }, [query, clear, close, onCreateAndAdd]);

  // Close on any press outside the picker. `touchstart` covers mobile Safari, where the
  // synthesized mousedown is unreliable (or never fires when the target scrolls).
  useEffect(() => {
    if (!isOpen) return;
    const onPressOutside = (e: Event) => {
      const target = e.target as Node | null;
      if (target && containerRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener("mousedown", onPressOutside);
    document.addEventListener("touchstart", onPressOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onPressOutside);
      document.removeEventListener("touchstart", onPressOutside);
    };
  }, [isOpen, close]);

  // When the keyboard opens the visible area shrinks; make sure the input (at the
  // bottom of the dialog body) is scrolled into the part of the dialog still on screen.
  useEffect(() => {
    if (!isOpen || !isKeyboardOpen) return;
    const timer = window.setTimeout(() => {
      inputRef.current?.scrollIntoView({ block: "nearest" });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [isOpen, isKeyboardOpen, visibleHeight]);

  const showDropdown = isOpen && (results.length > 0 || query.trim().length > 0);
  const canOfferCreate = hasSearched && !exactMatchExists && query.trim().length > 0;

  // The list opens upwards; cap it so it never grows past the visible area when the
  // keyboard has eaten most of the screen.
  const dropdownMaxHeight = isKeyboardOpen
    ? Math.max(DROPDOWN_MIN_HEIGHT_PX, Math.min(DROPDOWN_MAX_HEIGHT_PX, visibleHeight - DROPDOWN_RESERVED_PX))
    : DROPDOWN_MAX_HEIGHT_PX;

  return {
    containerRef,
    inputRef,
    query,
    setQuery,
    results,
    showDropdown,
    canOfferCreate,
    dropdownMaxHeight,
    open,
    close,
    pickFromCatalog,
    createAndAdd,
  };
};
